import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Col,
  Form,
  Input,
  Row,
  Select,
  Divider,
  Checkbox,
  Button,
  Card,
  Statistic,
} from "antd";

const { Option } = Select;

const StepItem = ({ form, formData, setFormData }) => {
  const [items, setItems] = useState(
    formData.items || [
      {
        description: "",
        commodityCode: "",
        quantity: 1,
        unit: "Pieces",
        value: 0,
        weight: 0,
      },
    ]
  );

  const [charges] = useState(
    formData.charges || [
      { type: "Freight/Shipping Charge/Fee/Cost", amount: 1.0 },
      { type: "Insurance Cost (Fee)", amount: 2.0 },
    ]
  );

  useEffect(() => {
    form.setFieldsValue(formData);
  }, [formData, form]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        description: "",
        commodityCode: "",
        quantity: 1,
        unit: "Pieces",
        value: 0,
        weight: 0,
      },
    ]);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
    setFormData((prev) => ({ ...prev, items: newItems }));
  };

  // const handleChargeChange = (index, field, value) => {
  //   const updatedCharges = [...charges];
  //   updatedCharges[index][field] =
  //     field === "amount" ? parseFloat(value) : value;
  //   setCharges(updatedCharges);
  //   setFormData((prev) => ({ ...prev, charges: updatedCharges }));
  // };

  // const handleRemoveCharge = (index) => {
  //   const updatedCharges = charges.filter((_, i) => i !== index);
  //   setCharges(updatedCharges);
  //   setFormData((prev) => ({ ...prev, charges: updatedCharges }));
  // };

  // const handleAddCharge = () => {
  //   setCharges([...charges, { type: "", amount: 0.0 }]);
  // };

  const calculateTotals = () => {
    const totalUnits = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
    const totalValue = items.reduce((sum, item) => sum + item.value, 0);
    return { totalUnits, totalWeight, totalValue };
  };

  const totals = calculateTotals();
  const totalInvoiceValue =
    charges.reduce((acc, charge) => acc + charge.amount, totals.totalValue) ||
    0;

  return (
    <Form form={form} layout="vertical">
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Form.Item label="What are you shipping?" name="shipmentType">
            <Select>
              <Option value="documents">Documents</Option>
              <Option value="packages">Packages</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Purpose of Shipment" name="purpose">
            <Select>
              <Option value="gift">Gift</Option>
              <Option value="business">Business</Option>
              <Option value="personal">Personal</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Checkbox>I would like to see estimated duties and taxes</Checkbox>
      <Divider />

      {items.map((item, index) => (
        <div key={index} style={{ marginBottom: "20px" }}>
          <h4>#Item {index + 1}</h4>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item label="Item Description">
                <Input
                  placeholder="Describe your item"
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(index, "description", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Commodity Code">
                <Input
                  placeholder="Enter commodity code"
                  value={item.commodityCode}
                  onChange={(e) =>
                    handleItemChange(index, "commodityCode", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item label="Quantity">
                <Input
                  type="number"
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", Number(e.target.value))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Units">
                <Select
                  value={item.unit}
                  onChange={(value) => handleItemChange(index, "unit", value)}
                >
                  <Option value="Pieces">Pieces</Option>
                  <Option value="Kg">Kg</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Value (Per Item)">
                <Input
                  prefix="$"
                  type="number"
                  value={item.value}
                  onChange={(e) =>
                    handleItemChange(index, "value", Number(e.target.value))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Weight (Per Item)">
                <Input
                  suffix="kg"
                  type="number"
                  value={item.weight}
                  onChange={(e) =>
                    handleItemChange(index, "weight", Number(e.target.value))
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
        </div>
      ))}

      <Button type="dashed" onClick={handleAddItem} style={{ width: "100%" }}>
        Add Item
      </Button>

      <Divider />
      <h4>Totals</h4>
      <Card bordered={false} style={{ marginBottom: "20px" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col span={8}>
            <Statistic
              title="Total Units"
              value={totals.totalUnits}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Weight"
              value={totals.totalWeight}
              suffix="KG"
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Value"
              value={totals.totalValue}
              prefix="$"
              valueStyle={{ color: "#f5222d" }}
            />
          </Col>
        </Row>
      </Card>

      <Card title="Invoice Value" style={{ marginTop: "20px" }}>
        <Row justify="space-between">
          <Col>
            <strong>Total Invoice Value:</strong>
          </Col>
          <Col>
            <strong style={{ fontSize: "18px", color: "#1890ff" }}>
              {totalInvoiceValue.toFixed(2)} USD
            </strong>
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

StepItem.propTypes = {
  form: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
};

export default StepItem;
